import React from 'react';

/**
 * Catches render-time errors so a single bad value cannot white-screen the checkout.
 *
 * There was previously no boundary anywhere in the app, which meant any throw during
 * render took down the entire page with no message. That risk is concrete here: the order
 * summary calls .toFixed() on prices coming from product-config, so one malformed catalog
 * entry would blank the page for every customer.
 *
 * Must be a class component: there is still no hook equivalent to componentDidCatch.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Deliberately logged without any form state: this component sits around a form
    // holding customer PII, and an error report is not a reason to print it.
    console.error('Unhandled render error:', error, errorInfo?.componentStack);
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="max-w-2xl mx-auto px-4 py-16 sm:py-24 text-center">
        <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Something went wrong</h1>
        <p className="mt-4 text-gray-600">
          We hit an unexpected error and didn&rsquo;t want to risk taking your payment details.
          Nothing has been charged. Please reload and try again, or contact us and we&rsquo;ll set
          your order up manually.
        </p>

        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            Reload the page
          </button>
          <a
            href="https://decisivesites.com/support/"
            rel="noopener"
            className="inline-flex items-center justify-center px-6 py-3 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
          >
            Contact support
          </a>
        </div>
      </div>
    );
  }
}
